/**
 * The games this browser played to the end, newest first: how a player did, kept so they can look
 * back. Per browser until the player signs in; then each finished seat is put on the account
 * (proven by its resume token) and history comes from the server on any device.
 */

import { useEffect } from 'react';
import type { Snapshot } from '@gsf/contract';
import { authApi } from './auth';
import { loadPlayer } from './storage';
import { accountNow, subscribe } from './whoami';

// whittle: localStorage while signed out; the account's copy lives on the server (`/auth/me/results`).
const KEY = 'pbt:results';
/** Enough to see a trend; older games drop off the end. */
const LIMIT = 50;

export interface PlayedGame {
  code: string;
  title: string;
  /** ms epoch the final screen was first seen. */
  finishedAt: number;
  score: number;
  /** 1-based, ties sharing a place. */
  rank: number;
  players: number;
  /**
   * The seat's resume token, the only proof this device sat there, kept until the seat is on an
   * account. Leaving a game clears the player's own copy, so this is the one that survives.
   */
  token?: string;
}

export function loadResults(): PlayedGame[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as PlayedGame[])
      .filter((g) => g && typeof g.code === 'string' && Number.isFinite(g.score))
      .sort((a, b) => b.finishedAt - a.finishedAt);
  } catch {
    return [];
  }
}

function write(games: PlayedGame[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(games.slice(0, LIMIT)));
  } catch {
    // Private browsing with storage disabled: there is simply no history.
  }
}

export function forgetResult(code: string): PlayedGame[] {
  const games = loadResults().filter((g) => g.code !== code);
  write(games);
  return games;
}

/**
 * Hand every unlinked finished seat to the signed-in account, then drop the tokens: the server
 * holds the history now, and a token left in storage is a credential nobody needs. A failure
 * keeps them for the next sign-in.
 */
export async function linkDeviceResults(): Promise<void> {
  const pending = loadResults().filter((g) => g.token);
  if (pending.length === 0) return;
  try {
    await authApi.linkSeats(pending.map((g) => ({ code: g.code, token: g.token! })));
  } catch {
    return;
  }
  write(loadResults().map(({ token: _sent, ...game }) => game));
}

// Link once per account this page sees: a sign-in, a registration, or a load while signed in.
let linkedFor: string | null = null;
subscribe(() => {
  const account = accountNow();
  if (!account || account.id === linkedFor) return;
  linkedFor = account.id;
  void linkDeviceResults();
});

/** Record this player's result once the show reaches its final screen. Re-recording is a no-op. */
export function useRecordResult(snapshot: Snapshot | null): void {
  const code = snapshot?.core.stage === 'results' ? snapshot.core.code : null;
  useEffect(() => {
    if (!snapshot || !code) return;
    const me = snapshot.core.show.standings.find((s) => s.target.id === snapshot.you.participantId);
    if (!me || loadResults().some((g) => g.code === code)) return;
    write([
      {
        code,
        title: snapshot.core.title,
        finishedAt: Date.now(),
        // What every screen's final results show, so history agrees with the room.
        score: me.showTotal,
        rank: me.rank,
        players: snapshot.core.show.standings.length,
        token: loadPlayer(code)?.token,
      },
      ...loadResults(),
    ]);
    // Signed in mid-game: the seat was joined anonymously, so link it now.
    if (accountNow()) void linkDeviceResults();
    // Once per finished game: the snapshot keeps changing on the results screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);
}
